import { Component, signal, computed, effect } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { ReportsService } from 'src/app/core/services/reports.service';
import { AppNotificationService } from 'src/app/shared/services/app-notification.service';
import { SessionStorageKeys, StoreService } from 'src/app/core/services/store.service';
import { Design } from 'src/app/shared/models/design.model';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PerivisReportOptionsDialogComponent } from './perivis-report-options-dialog.component';
import { CombinedReportOptionsDialogComponent } from './combined-report-options-dialog.component';
import { CombinedReportDefaults, CombinedReportOptions, CombinedReportSectionToggles } from '../../models/combined-report-options.model';
import { PeriformInputsReportPresets } from '../../models/perifor-inputs-report-options.model';
import { PerivisReportOptions } from '../../models/perivis-report-options.model';
import { PericalReportOptionsDialogComponent } from './perical-report-options-dialog.component';
import { PericalReportOptions } from '../../models/perical-report-options.model';
import { PeriformInputsReportOptionsDialogComponent } from './perifor-inputs-report-options-dialog.component';
import { PeriformInputsReportOptions } from '../../models/perifor-inputs-report-options.model';

interface Report {
  name: string;
  endpoint: string;
  description?: string;
  isLoading: boolean;
  isSetupLoading?: boolean;
}

interface ReportGroup {
  suffixLabel: string;
  labelColor: string;
  reports: Report[];
}

interface SavedReportOptions {
  perivisResults?: PerivisReportOptions;
  pericalResults?: PericalReportOptions;
  periforInputs?: PeriformInputsReportOptions;
  combinedReport?: CombinedReportSectionToggles;
}

interface DesignScopedReportOptions {
  perivisResults?: PerivisReportOptions;
  pericalResults?: PericalReportOptions;
}

interface GlobalReportOptions {
  periforInputs?: PeriformInputsReportOptions;
  combinedReport?: CombinedReportSectionToggles;
}

const REPORT_OPTIONS_GLOBAL_STORAGE_KEY = 'reports-user-options-global';
const REPORT_OPTIONS_DESIGN_STORAGE_KEY_PREFIX = 'reports-user-options-design';

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
  providers: [DialogService],
  standalone: false
})
export class ReportsComponent {
  private optionsDialogRef: DynamicDialogRef | null = null;
  private readonly configurableReportEndpoints = ['perivis-results-report', 'perical-results-report', 'perifor-inputs', 'combined-report'];
  private readonly invalidCharsRegex = /[^\w\s-]/g;
  private readonly pericalReportEndpoints = ['perical-operations-summary', 'perical-results-report'];
  private readonly perivisReportEndpoints = ['perivis-load-case-summary', 'perivis-results-report'];

  // Signals for reactive state management
  public designId = toSignal(this._route.params.pipe(map(params => params['designId'])));
  public designName = signal<string>('Loading...');
  public reports = signal<Report[]>([
    {
      name: 'Inputs',
      endpoint: 'perifor-inputs',
      isLoading: false,
      isSetupLoading: false
    },
    {
      name: 'Operations Summary',
      endpoint: 'perical-operations-summary',
      isLoading: false,
      isSetupLoading: false
    },
    {
      name: 'Results Report',
      endpoint: 'perical-results-report',
      isLoading: false,
      isSetupLoading: false
    },
    {
      name: 'Load Case Summary',
      endpoint: 'perivis-load-case-summary',
      isLoading: false,
      isSetupLoading: false
    },
    {
      name: 'Results Report',
      endpoint: 'perivis-results-report',
      isLoading: false,
      isSetupLoading: false
    }
  ]);

  // Computed signal for checking if design is selected
  public hasDesign = computed(() => !!this.designId());
  public savedReportOptions = signal<SavedReportOptions>({});
  public combinedReport = signal<Report>({
    name: 'Combined Report',
    endpoint: 'combined-report',
    isLoading: false,
    isSetupLoading: false
  });
  public periforReports = computed(() => [...this.periforInputReports(), this.combinedReport()]);
  public periforInputReports = computed(() => this.reports().filter(report => report.endpoint === 'perifor-inputs'));
  public pericalReports = computed(() => this.reports().filter(report => this.pericalReportEndpoints.includes(report.endpoint)));
  public perivisReports = computed(() => this.reports().filter(report => this.perivisReportEndpoints.includes(report.endpoint)));
  public reportGroups = computed<ReportGroup[]>(() => [
    {
      suffixLabel: 'FOR',
      labelColor: 'red',
      reports: this.periforReports()
    },
    {
      suffixLabel: 'CAL',
      labelColor: '#ff5722',
      reports: this.pericalReports()
    },
    {
      suffixLabel: 'VIS',
      labelColor: '#00e5ff',
      reports: this.perivisReports()
    }
  ]);

  constructor(
    private _route: ActivatedRoute,
    private _reportsService: ReportsService,
    private _toaster: AppNotificationService,
    private _storeService: StoreService,
    private _dialogService: DialogService
  ) {
    // Load design name from session storage when designId changes
    effect(async () => {
      const designId = this.designId();
      if (designId) {
        const design = await this._storeService.getSession<Design>(SessionStorageKeys.DESIGN);
        this.designName.set(design?.name || 'Unknown Design');
      }
    });

    // PERIVIS/PERICAL settings are design-scoped, so refresh when route design changes.
    effect(() => {
      this.designId();
      void this.loadSavedReportOptions();
    });
  }

  public canConfigureReport(report: Report): boolean {
    return this.configurableReportEndpoints.includes(report.endpoint);
  }

  public hasSavedSetup(report: Report): boolean {
    if (report.endpoint === 'perivis-results-report') {
      return !!this.savedReportOptions().perivisResults;
    }

    if (report.endpoint === 'perical-results-report') {
      return !!this.savedReportOptions().pericalResults;
    }

    if (report.endpoint === 'perifor-inputs') {
      return !!this.savedReportOptions().periforInputs;
    }

    if (report.endpoint === 'combined-report') {
      return !!this.savedReportOptions().combinedReport;
    }

    return true;
  }

  public getSetupStatusLabel(report: Report): string {
    if (!this.canConfigureReport(report)) {
      return '';
    }

    return this.hasSavedSetup(report) ? 'Setup saved' : 'Not configured';
  }

  public async setupReport(report: Report): Promise<void> {
    if (!this.designId()) {
      this._toaster.showError('No design selected. Please select a design first.');
      return;
    }

    if (report.endpoint === 'perivis-results-report') {
      this.openPerivisReportOptionsDialog();
      return;
    }

    if (report.endpoint === 'perical-results-report') {
      await this.openPericalReportOptionsDialog();
      return;
    }

    if (report.endpoint === 'perifor-inputs') {
      this.openPeriformInputsReportOptionsDialog();
      return;
    }

    if (report.endpoint === 'combined-report') {
      this.openCombinedReportOptionsDialog();
      return;
    }

    this._toaster.showInfo('This report has no configurable setup options.');
  }

  public async downloadReport(report: Report): Promise<void> {
    if (!this.designId()) {
      this._toaster.showError('No design selected. Please select a design first.');
      return;
    }

    if (report.endpoint === 'perivis-results-report') {
      const perivisOptions = this.savedReportOptions().perivisResults;
      if (!perivisOptions) {
        this._toaster.showError('No setup saved for PERIVIS Results Report. Click Setup first.');
        return;
      }
      await this.executeReportDownload(report, perivisOptions);
      return;
    }

    if (report.endpoint === 'perical-results-report') {
      const pericalOptions = this.savedReportOptions().pericalResults;
      if (!pericalOptions) {
        this._toaster.showError('No setup saved for PERICAL Results Report. Click Setup first.');
        return;
      }
      await this.executeReportDownload(report, pericalOptions);
      return;
    }

    if (report.endpoint === 'perifor-inputs') {
      const periforInputsOptions = this.savedReportOptions().periforInputs;
      if (!periforInputsOptions) {
        this._toaster.showError('No setup saved for PERIFOR Inputs Report. Click Setup first.');
        return;
      }
      await this.executeReportDownload(report, periforInputsOptions);
      return;
    }

    if (report.endpoint === 'combined-report') {
      const toggles = this.savedReportOptions().combinedReport ?? CombinedReportDefaults.SectionToggles;
      const combinedOptions: CombinedReportOptions = {
        includePeriforInputs: toggles.includePeriforInputs,
        periforInputsOptions: this.savedReportOptions().periforInputs ?? PeriformInputsReportPresets.All,
        includePericalOpSummary: toggles.includePericalOpSummary,
        includePerivisLoadCaseSummary: toggles.includePerivisLoadCaseSummary,
        includePericalResults: toggles.includePericalResults,
        pericalResultsOptions: this.savedReportOptions().pericalResults ?? null,
        includePerivisResults: toggles.includePerivisResults,
        perivisResultsOptions: this.savedReportOptions().perivisResults ?? null,
      };
      await this.executeReportDownload(report, combinedOptions as any);
      return;
    }

    await this.executeReportDownload(report, null);
  }

  private async loadSavedReportOptions(): Promise<void> {
    const designId = this.designId();
    const globalOptions = await this._storeService.get<GlobalReportOptions>(REPORT_OPTIONS_GLOBAL_STORAGE_KEY);

    let designScopedOptions: DesignScopedReportOptions | null = null;
    if (designId) {
      designScopedOptions = await this._storeService.get<DesignScopedReportOptions>(
        this.getDesignReportOptionsStorageKey(designId)
      );
    }

    this.savedReportOptions.set({
      perivisResults: designScopedOptions?.perivisResults,
      pericalResults: designScopedOptions?.pericalResults,
      periforInputs: globalOptions?.periforInputs,
      combinedReport: globalOptions?.combinedReport
    });
  }

  private async saveReportOptions(options: SavedReportOptions): Promise<void> {
    this.savedReportOptions.set(options);

    const globalOptions: GlobalReportOptions = {
      periforInputs: options.periforInputs,
      combinedReport: options.combinedReport
    };
    await this._storeService.set<GlobalReportOptions>(REPORT_OPTIONS_GLOBAL_STORAGE_KEY, globalOptions);

    const designId = this.designId();
    if (designId) {
      const designScopedOptions: DesignScopedReportOptions = {
        perivisResults: options.perivisResults,
        pericalResults: options.pericalResults
      };
      await this._storeService.set<DesignScopedReportOptions>(
        this.getDesignReportOptionsStorageKey(designId),
        designScopedOptions
      );
    }
  }

  private getDesignReportOptionsStorageKey(designId: string): string {
    return `${REPORT_OPTIONS_DESIGN_STORAGE_KEY_PREFIX}:${designId}`;
  }

  private setReportLoading(endpoint: string, isLoading: boolean): void {
    this.reports.update(reports =>
      reports.map(r => r.endpoint === endpoint ? { ...r, isLoading } : r)
    );
    if (this.combinedReport().endpoint === endpoint) {
      this.combinedReport.update(r => ({ ...r, isLoading }));
    }
  }

  private setReportSetupLoading(endpoint: string, isSetupLoading: boolean): void {
    this.reports.update(reports =>
      reports.map(r => r.endpoint === endpoint ? { ...r, isSetupLoading } : r)
    );
    if (this.combinedReport().endpoint === endpoint) {
      this.combinedReport.update(r => ({ ...r, isSetupLoading }));
    }
  }

  private async openPerivisReportOptionsDialog(): Promise<void> {
    this.setReportSetupLoading('perivis-results-report', true);

    let headersData: any = null;
    try {
      headersData = await this._reportsService.getPerivisHeaders(this.designId()!);
    } catch {
      // proceed without headers — load case dropdowns will be empty
    } finally {
      this.setReportSetupLoading('perivis-results-report', false);
    }

    this.optionsDialogRef = this._dialogService.open(PerivisReportOptionsDialogComponent, {
      header: 'Configure PERIVIS Results Report',
      width: '800px',
      data: {
        preset: 'none',
        initialOptions: this.savedReportOptions().perivisResults ?? null,
        headersData: headersData
      }
    });

    this.optionsDialogRef.onClose.subscribe((options: PerivisReportOptions | null) => {
      if (options) {
        this.saveReportOptions({
          ...this.savedReportOptions(),
          perivisResults: options
        });
        this._toaster.showSuccess('PERIVIS report setup saved.');
      }
    });
  }

  private openPeriformInputsReportOptionsDialog(): void {
    this.optionsDialogRef = this._dialogService.open(PeriformInputsReportOptionsDialogComponent, {
      header: 'Configure PERIFOR Inputs Report',
      width: '700px',
      data: {
        preset: 'none',
        initialOptions: this.savedReportOptions().periforInputs ?? null
      }
    });

    this.optionsDialogRef.onClose.subscribe((options: PeriformInputsReportOptions | null) => {
      if (options) {
        this.saveReportOptions({
          ...this.savedReportOptions(),
          periforInputs: options
        });
        this._toaster.showSuccess('PERIFOR Inputs report setup saved.');
      }
    });
  }

  private openCombinedReportOptionsDialog(): void {
    this.combinedReport.update(r => ({ ...r, isSetupLoading: true }));

    this.optionsDialogRef = this._dialogService.open(CombinedReportOptionsDialogComponent, {
      header: 'Configure Combined Report',
      width: '500px',
      data: {
        initialToggles: this.savedReportOptions().combinedReport ?? null,
        hasPeriforInputsSetup: !!this.savedReportOptions().periforInputs,
        hasPericalResultsSetup: !!this.savedReportOptions().pericalResults,
        hasPerivisResultsSetup: !!this.savedReportOptions().perivisResults,
      }
    });

    this.optionsDialogRef.onClose.subscribe((toggles: CombinedReportSectionToggles | null) => {
      this.combinedReport.update(r => ({ ...r, isSetupLoading: false }));
      if (toggles) {
        this.saveReportOptions({
          ...this.savedReportOptions(),
          combinedReport: toggles
        });
        this._toaster.showSuccess('Combined report setup saved.');
      }
    });
  }

  private async openPericalReportOptionsDialog(): Promise<void> {
    this.setReportSetupLoading('perical-results-report', true);

    try {
      const headersData = await this._reportsService.getPericalHeaders(this.designId()!);
      
      this.optionsDialogRef = this._dialogService.open(PericalReportOptionsDialogComponent, {
        header: 'Configure PERICAL Results Report',
        width: '800px',
        data: {
          preset: 'none',
          initialOptions: this.savedReportOptions().pericalResults ?? null,
          headersData: headersData
        }
      });

      this.optionsDialogRef.onClose.subscribe((options: PericalReportOptions | null) => {
        if (options) {
          this.saveReportOptions({
            ...this.savedReportOptions(),
            pericalResults: options
          });
          this._toaster.showSuccess('PERICAL report setup saved.');
        }
      });
    } catch {
      this.optionsDialogRef = this._dialogService.open(PericalReportOptionsDialogComponent, {
        header: 'Configure PERICAL Results Report',
        width: '800px',
        data: {
          preset: 'none',
          initialOptions: this.savedReportOptions().pericalResults ?? null,
          headersData: null
        }
      });

      this.optionsDialogRef.onClose.subscribe((options: PericalReportOptions | null) => {
        if (options) {
          this.saveReportOptions({
            ...this.savedReportOptions(),
            pericalResults: options
          });
          this._toaster.showSuccess('PERICAL report setup saved.');
        }
      });
    } finally {
      this.setReportSetupLoading('perical-results-report', false);
    }
  }

  private async executeReportDownload(report: Report, options: PerivisReportOptions | PericalReportOptions | PeriformInputsReportOptions | null): Promise<void> {
    this.setReportLoading(report.endpoint, true);

    try {
      const blob = await this._reportsService.downloadReport(
        this.designId()!, 
        report.endpoint.replace('-report', ''), // Remove '-report' suffix for API
        options
      );
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = this.buildReportFileName(report, this.designName(), 'docx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this._toaster.showSuccess(`${report.name} downloaded successfully`);
    } catch (error) {
      console.error('Error downloading report:', error);
      this._toaster.showError(`Failed to download ${report.name}`);
    } finally {
      this.setReportLoading(report.endpoint, false);
    }
  }

  private buildReportFileName(report: Report, designName: string, extension: string): string {
    const timestamp = this.getUtcTimestamp();
    const modulePrefix = this.getModulePrefix(report.endpoint);
    const documentType = this.toSafeSegment(report.name);
    const safeDesignName = this.toSafeSegment(designName) || 'Design';
    const safeExtension = extension.replace(/^\.+/, '');
    return `${safeDesignName}_${modulePrefix}_${documentType}_${timestamp}.${safeExtension}`;
  }

  private getModulePrefix(endpoint: string): string {
    if (endpoint.startsWith('perivis-')) {
      return 'PERIVIS';
    }

    if (endpoint.startsWith('perical-')) {
      return 'PERICAL';
    }

    if (endpoint.startsWith('perifor-')) {
      return 'PERIFOR';
    }

    if (endpoint === 'combined-report') {
      return '';
    }

    return 'REPORT';
  }

  private getUtcTimestamp(): string {
    return new Date()
      .toISOString()
      .replace(/[-:]/g, '')
      .replace('T', '_')
      .slice(0, 15);
  }

  private toSafeSegment(value: string): string {
    return value
      .trim()
      .replace(this.invalidCharsRegex, '')
      .replace(/\s*-\s*/g, '-')
      .replace(/\s+/g, '_');
  }
}
